import { differenceInCalendarDays, format, isSameMonth, isSameYear } from 'date-fns';

/** A picked range. `to` is unset while only the start is chosen. */
export type DateRange = { from: Date | undefined; to?: Date | undefined };

/** "23 Sep 2026" - the Figma display format for a date (Data/Medium, tabular). */
export const formatDate = (date: Date) => format(date, 'd MMM yyyy');

/**
 * "14 – 20 Sep 2026", "28 Sep – 4 Oct 2026", "28 Dec 2026 – 3 Jan 2027".
 * A range without an end shows its start only.
 */
export function formatDateRange(range: DateRange | undefined): string {
  const { from, to } = range ?? { from: undefined };
  if (!from) return '';
  if (!to) return `${formatDate(from)} –`;
  if (!isSameYear(from, to)) return `${formatDate(from)} – ${formatDate(to)}`;
  if (!isSameMonth(from, to))
    return `${format(from, 'd MMM')} – ${format(to, 'd MMM yyyy')}`;
  return `${format(from, 'd')} – ${format(to, 'd MMM yyyy')}`;
}

/** Date Picker footer summary: "14 – 20 Sep · 7 days". */
export function formatRangeSummary(range: DateRange | undefined): string {
  const { from, to } = range ?? { from: undefined };
  if (!from) return 'Pick a start date';
  if (!to) return `${format(from, 'd MMM')} – pick an end date`;
  const days = differenceInCalendarDays(to, from) + 1;
  const span = isSameMonth(from, to)
    ? `${format(from, 'd')} – ${format(to, 'd MMM')}`
    : `${format(from, 'd MMM')} – ${format(to, 'd MMM')}`;
  return `${span} · ${days} ${days === 1 ? 'day' : 'days'}`;
}

/** Why typed text isn't a usable value. Shown in the Field error. */
export type DateInputError =
  'invalid-format' | 'nonexistent' | 'unavailable' | 'end-before-start';

export const DATE_INPUT_MESSAGES: Record<DateInputError, string> = {
  'invalid-format': 'Use a date like 23 Sep 2026 or 2026-09-23',
  nonexistent: 'That date doesn’t exist',
  unavailable: 'That date isn’t available',
  'end-before-start': 'The end date is before the start date',
};

const MONTHS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];

type Parts = { day: number; month?: number; year?: number };
type Parsed<T, E = DateInputError> = { ok: true; value: T } | { ok: false; error: E };

const monthIndex = (name: string) => MONTHS.indexOf(name.slice(0, 3).toLowerCase()) + 1;

/** "23 Sep 2026", "Sep 23, 2026", "2026-09-23"; in a range, "14" or "14 Sep" too. */
function parseParts(text: string, partial: boolean): Parts | null {
  const t = text.trim().replace(/\s+/g, ' ');
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (m) return { year: +m[1]!, month: +m[2]!, day: +m[3]! };
  m = /^(\d{1,2}) ([a-z]{3,})\.?,?(?: (\d{4}))?$/i.exec(t);
  if (m && monthIndex(m[2]!) && (m[3] || partial))
    return { day: +m[1]!, month: monthIndex(m[2]!), year: m[3] ? +m[3] : undefined };
  m = /^([a-z]{3,})\.? (\d{1,2}),?(?: (\d{4}))?$/i.exec(t);
  if (m && monthIndex(m[1]!) && (m[3] || partial))
    return { day: +m[2]!, month: monthIndex(m[1]!), year: m[3] ? +m[3] : undefined };
  m = /^(\d{1,2})$/.exec(t);
  if (m && partial) return { day: +m[1]! };
  return null;
}

function toDate({ day, month, year }: Required<Parts>): Parsed<Date> {
  const date = new Date(year, month - 1, day);
  const exists =
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day;
  return exists ? { ok: true, value: date } : { ok: false, error: 'nonexistent' };
}

/** Parse a typed date ("23 Sep 2026", "2026-09-23", "Sep 23, 2026"). */
export function parseDateInput(text: string): Parsed<Date> {
  const parts = parseParts(text, false);
  if (!parts) return { ok: false, error: 'invalid-format' };
  return toDate(parts as Required<Parts>);
}

/**
 * Parse a typed range: "14 – 20 Sep 2026", "28 Sep – 4 Oct 2026",
 * "2026-09-14 - 2026-09-20". The start borrows a missing month / year from
 * the end.
 */
export function parseDateRangeInput(text: string): Parsed<{ from: Date; to: Date }> {
  const halves = text.split(/\s+[–—-]\s+|\s+to\s+/i);
  if (halves.length !== 2) return { ok: false, error: 'invalid-format' };
  const end = parseParts(halves[1]!, false);
  const start = parseParts(halves[0]!, true);
  if (!start || !end) return { ok: false, error: 'invalid-format' };
  const to = toDate(end as Required<Parts>);
  const from = toDate({
    day: start.day,
    month: start.month ?? end.month!,
    year: start.year ?? end.year!,
  });
  if (!from.ok) return from;
  if (!to.ok) return to;
  if (to.value < from.value) return { ok: false, error: 'end-before-start' };
  return { ok: true, value: { from: from.value, to: to.value } };
}

/** Why typed text isn't a time. */
export type TimeInputError = 'invalid-format' | 'nonexistent';

export const TIME_INPUT_MESSAGES: Record<TimeInputError, string> = {
  'invalid-format': 'Use a time like 14:30',
  nonexistent: 'That time doesn’t exist',
};

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Parse a typed time into 24-hour "HH:mm": "14:30", "14.30", "1430", "9",
 * "2:30 pm", "2pm". "25:30" doesn't exist.
 */
export function parseTimeInput(text: string): Parsed<string, TimeInputError> {
  const m = /^(\d{1,2})(?:[:.h]?(\d{2}))?\s*([ap]\.?m\.?)?$/i.exec(
    text.trim().replace(/\s+/g, ' '),
  );
  if (!m) return { ok: false, error: 'invalid-format' };
  let hour = +m[1]!;
  const minute = m[2] ? +m[2] : 0;
  const meridiem = m[3]?.[0]?.toLowerCase();
  if (meridiem) {
    if (hour < 1 || hour > 12) return { ok: false, error: 'nonexistent' };
    hour = (hour % 12) + (meridiem === 'p' ? 12 : 0);
  }
  if (hour > 23 || minute > 59) return { ok: false, error: 'nonexistent' };
  return { ok: true, value: `${pad(hour)}:${pad(minute)}` };
}

/** Every slot of the day in `step`-minute steps: "00:00", "00:30", … */
export function timeSlots(step = 30): string[] {
  const slots: string[] = [];
  for (let t = 0; t < 24 * 60; t += step)
    slots.push(`${pad(Math.floor(t / 60))}:${pad(t % 60)}`);
  return slots;
}
